import { AppTable, SlColumn, SlRow } from '@stackline/ui';
import React, { useEffect, useState } from 'react';
import { AdPortfolioEntity } from 'sl-api-connector/types';
import numeral from 'numeral';
import ReduxStore from 'src/types/store/reduxStore';

import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { calculatePercentChange } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { computeDifference } from 'src/utils/metrics';
import { getStrategyDisplayInfo } from 'src/components/AdManager/AmsUtils';
import _get from 'lodash/get';
import { getCompareValue, VolumnEditCell } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { useDispatch, useSelector } from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import { setPortfolioArray } from 'src/store/modules/adManager/adBudgetEditor/actions';
import _set from 'lodash/set';
import _startCase from 'lodash/startCase';

const GRID_ITEMS_PER_PAGE = 12;

const BudgetEditCell = ({
  portfolio
}: {
  portfolio: ReduxStore['adPortfoliosData'] & {
    entity: AdPortfolioEntity & {
      extendedAttributes: any;
    };
    id: string;
    isTopRow?: boolean;
    mtdSpend?: number;
    newBudgetSum: number;
  };
}) => {
  const timeline = useSelector((state: ReduxStore) => state.adBudgetEditor.timeline);
  const dispatch = useDispatch();
  const adBudgetEditor = useSelector((state: ReduxStore) => state.adBudgetEditor);
  const handleBudgetChange = (value: string) => {
    const clonePortfolios = _cloneDeep(adBudgetEditor.portfolioArray);
    clonePortfolios.forEach((item) => {
      if (item.id === portfolio.id) {
        _set(item, 'entity.extendedAttributes.isSelected', true);
        _set(item, 'entity.extendedAttributes.newBudgetSetting.amount', +value);
      }
    });
    dispatch(setPortfolioArray(clonePortfolios));
  };

  const detaultValue = adBudgetEditor?.portfolioArray?.find((item) => item.id === portfolio.id)?.entity
    ?.extendedAttributes.newBudgetSetting?.amount;

  const minimumBudget =
    timeline.name === 'currentMonthBudgetSetting' && portfolio.mtdSpend ? Math.max(portfolio.mtdSpend, 1) : 1;

  const currentBudget = portfolio?.entity?.extendedAttributes?.liveBudgetSetting?.amount || 0;
  const placeholderText = currentBudget ? numeral(currentBudget).format('1,000') : '0';

  if (portfolio?.isTopRow) {
    return <div style={{ width: 104, fontWeight: 700 }}>{numeral(portfolio.newBudgetSum).format('$1,000')}</div>;
  }
  return (
    <div style={{ width: 104 }} key={portfolio.id}>
      <VolumnEditCell
        min={minimumBudget}
        minErrorMessage={(value) => {
          return value !== '0'
            ? 'Your new budget cannot be less than MTD ad spend. Please increase your budget.'
            : 'Your new  budget cannot be $0. Please increase your budget.';
        }}
        baseWidth={104}
        prefix="$"
        defaultValue={detaultValue?.toString()}
        placeholder={placeholderText}
        saveValue={handleBudgetChange}
      />
    </div>
  );
};

interface AllocationGridProps {
  data: any[];
  entityTableHeaderProps: Omit<
    EntityTableHeaderProps,
    'enableSwitchingLayouts' | 'defaultView' | 'verticalInset' | 'handleChangeLayout' | 'eventBus'
  >;
  loading?: boolean;
  rowCount?: number;
  appTableProps?: Partial<React.ComponentProps<typeof AppTable>>;
  topRow?: any;
}

const portfolioColumnDef = {
  field: 'portfolioName',
  headerName: 'Portfolio',
  width: 250,
  valueFormatter: (_, row) => {
    if (row?.isTopRow) {
      return <div style={{ width: 200, fontWeight: 700 }}>Total</div>;
    }
    return (
      <div
        style={{
          width: 200
        }}
      >
        {row?.entity?.displayName || '--'}
      </div>
    );
  }
};

const l30dRoasColumnDef = {
  field: 'l30dRoas',
  headerName: 'L30D\nROAS',
  valueFormatter: (_, row) => {
    if (!row?.additionalValues) {
      return <div style={{ width: 60 }}></div>;
    }

    const currentRoas = row?.additionalValues?.returnOnAdSpend_computed_value;
    const comparisonRoas = getCompareValue(row?.additionalValues, 'returnOnAdSpend_computed_value_');

    const difference = computeDifference(currentRoas, comparisonRoas);

    return (
      <div style={{ width: 60 }}>
        <MetricCell
          value={currentRoas}
          metricType={METRICTYPE.MONEY}
          percentChange={calculatePercentChange(currentRoas, currentRoas - difference) || undefined}
        />
      </div>
    );
  }
};

const currentBudgetColumnDef = {
  field: 'currentBudget',
  headerName: 'Current\nBudget',
  valueFormatter: (_, row) => {
    const amount = row?.entity?.extendedAttributes?.liveBudgetSetting?.amount || 0;
    if (row?.isTopRow) {
      return <div style={{ width: 75, fontWeight: 700 }}>{numeral(row?.currentBudgetSum).format('$1,000')}</div>;
    }
    return <div style={{ width: 75 }}>{numeral(amount).format('$1,000')}</div>;
  }
};

const newBudgetColumnDef = {
  field: 'newBudget',
  headerName: 'New\nBudget',
  valueFormatter: (_, row) => {
    return <BudgetEditCell portfolio={row} />;
  }
};

const mtdAdSpendColumnDef = {
  field: 'mtdAdSpend',
  headerName: 'MTD\nAd Spend',
  valueFormatter: (_, row) => {
    const amount = row?.mtdSpend || 0;
    if (row?.isTopRow) {
      return <div style={{ width: 75, fontWeight: 700 }}>{numeral(row?.mtdSpendSum).format('$1,000')}</div>;
    }
    return <div style={{ width: 75 }}>{numeral(amount).format('$1,000')}</div>;
  }
};

const projectedSpendColumnDef = {
  field: 'projectedSpend',
  headerName: 'Projected\nSpend',
  valueFormatter: (_, row) => {
    const amount = row?.projectedSpend || 0;
    if (row?.isTopRow) {
      return <div style={{ width: 75, fontWeight: 700 }}>{numeral(row?.projectedSpendSum).format('$1,000')}</div>;
    }
    return <div style={{ width: 75 }}>{numeral(amount).format('$1,000')}</div>;
  }
};

const automationColumnDef = {
  field: 'automation',
  headerName: 'Automation\nType',
  valueFormatter: (_, row) => {
    const { displayName: statusName, color: statusColor } = _get(row?.entity, 'extendedAttributes.statusDisplay', {
      displayName: '',
      color: ''
    });

    const { displayName: strategyName } = getStrategyDisplayInfo(row?.entity?.extendedAttributes);
    if (row?.isTopRow) {
      return <div style={{ width: 200, fontWeight: 700 }}></div>;
    }
    return (
      <div style={{ width: 200 }}>
        <div>{strategyName || '--'}</div>
        <div style={{ color: statusColor }}>{_startCase(statusName)}</div>
      </div>
    );
  }
};

const columnDefs = [
  portfolioColumnDef,
  l30dRoasColumnDef,
  currentBudgetColumnDef,
  newBudgetColumnDef,
  mtdAdSpendColumnDef,
  projectedSpendColumnDef,
  automationColumnDef
];

const AllocationGrid = ({ data, loading, appTableProps = {}, topRow }: AllocationGridProps) => {
  const [page, setPage] = useState(1);

  const hidePagination = (data || []).length < GRID_ITEMS_PER_PAGE && page === 1 && !loading;
  const totalPages = Math.max(Math.ceil((data?.length || 0) / GRID_ITEMS_PER_PAGE), 1); // Always show page 1 of 1 if no results exist

  const handleNextPage = () => {
    const adjustedPage = page - 1 + 1;
    const startIndex = (adjustedPage - 1) * GRID_ITEMS_PER_PAGE;
    const endIndex = startIndex + GRID_ITEMS_PER_PAGE;

    if (data.slice(startIndex, endIndex)?.length < GRID_ITEMS_PER_PAGE) {
      return;
    }
    const newPage = page + 1;
    setPage(newPage);
  };

  // go to the first page when sortOption changes or the time period options change
  useEffect(() => {
    setPage(1);
  }, []);

  const handlePrevPage = () => {
    const newPage = Math.max(1, page - 1);
    setPage(newPage);
  };

  return (
    <SlColumn widths="full" spacing="lg">
      <SlColumn widths="full">
        <div style={{ marginTop: '32px' }}>
          <EntityTable
            skeletonRows={GRID_ITEMS_PER_PAGE}
            isLoading={loading}
            page={page}
            setPage={setPage}
            showPagination={false}
            shouldModifyColumnDefs={false}
            columnDefs={columnDefs}
            initialPageSize={GRID_ITEMS_PER_PAGE}
            rowData={data || []}
            appTableProps={appTableProps}
            topRow={topRow}
          />
        </div>
        <div style={{}}>
          {hidePagination ? null : (
            <SlRow horizontalPosition="end">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onClickNext={() => handleNextPage()}
                onClickPrevious={() => handlePrevPage()}
              />
            </SlRow>
          )}
        </div>
      </SlColumn>
    </SlColumn>
  );
};

export default AllocationGrid;
